import type { AdminUserSummary } from '@/api';
import { formatRelativeAge, formatTokenCount } from '@/lib/format';
import { formatCountdown } from '@/lib/tempAccount';
import { type SortKey, accountDay, formatUserCost } from '@/lib/userStats';
import { cn } from '@/lib/utils';
import {
  ArrowDown,
  ArrowUp,
  Eraser,
  Loader2,
  ScrollText,
  Trash2
} from 'lucide-react';

import type { UserRowHandlers } from '@/components/admin/userRow';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

type Props = {
  rows: AdminUserSummary[];
  sortKey: SortKey;
  onSort: (key: SortKey) => void;
  handlersFor: (row: AdminUserSummary) => UserRowHandlers;
};

// A header with several sortKeys cycles through them on each click, so "Joined"
// toggles newest <-> oldest instead of committing to one direction.
const COLUMNS: {
  label: string;
  sortKeys: SortKey[];
  className?: string;
}[] = [
  { label: 'User', sortKeys: ['identifier'] },
  { label: 'Role', sortKeys: [] },
  { label: 'Joined', sortKeys: ['newest', 'oldest'] },
  { label: 'Last login', sortKeys: ['recent-login'] },
  { label: 'Tokens', sortKeys: ['tokens'], className: 'text-right' },
  { label: 'Cost', sortKeys: [], className: 'text-right' },
  { label: '', sortKeys: [], className: 'text-right' }
];

const ASCENDING_KEYS: SortKey[] = ['oldest', 'identifier'];

/**
 * Dense sortable user table — the desktop list surface.
 *
 * Role and status collapse to badges and the switches move to the detail dialog,
 * which already has them; only the three actions with no equivalent there —
 * audit log, purge, delete — stay in the row.
 */
export default function UserTable({
  rows,
  sortKey,
  onSort,
  handlersFor
}: Props) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {COLUMNS.map((column) => {
              const at = column.sortKeys.indexOf(sortKey);
              const active = at >= 0;
              return (
                <TableHead
                  key={column.label || 'actions'}
                  className={cn('h-8', column.className)}
                >
                  {column.sortKeys.length ? (
                    <button
                      type="button"
                      onClick={() =>
                        onSort(
                          column.sortKeys[(at + 1) % column.sortKeys.length]
                        )
                      }
                      className={cn(
                        'inline-flex items-center gap-1 hover:text-foreground',
                        active && 'text-foreground'
                      )}
                    >
                      {column.label}
                      {active ? (
                        ASCENDING_KEYS.includes(sortKey) ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : null}
                    </button>
                  ) : (
                    column.label
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const handlers = handlersFor(row);
            const anyBusy = handlers.busy !== null;
            const joined = accountDay(row.createdAt);
            return (
              <TableRow
                key={row.id}
                role="button"
                tabIndex={0}
                onClick={handlers.onOpenDetail}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handlers.onOpenDetail();
                  }
                }}
                className="cursor-pointer"
              >
                <TableCell className="max-w-[16rem] py-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate font-medium">
                      {row.identifier}
                    </span>
                    {handlers.isSelf ? (
                      <Badge variant="outline" className="text-[10px]">
                        you
                      </Badge>
                    ) : null}
                  </div>
                  <span className="text-[10px] text-muted-foreground">
                    {row.provider || 'unknown provider'}
                  </span>
                </TableCell>
                <TableCell className="py-1.5">
                  <div className="flex flex-wrap items-center gap-1">
                    {row.role === 'admin' ? (
                      <Badge className="text-[10px]">admin</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        user
                      </span>
                    )}
                    {row.disabled ? (
                      <Badge variant="destructive" className="text-[10px]">
                        disabled
                      </Badge>
                    ) : null}
                    {row.is_temporary ? (
                      <Badge variant="secondary" className="text-[10px]">
                        temp · {formatCountdown(row.temp_expires_at)}
                      </Badge>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="py-1.5 font-mono text-xs tabular-nums text-muted-foreground">
                  {joined ?? '—'}
                </TableCell>
                <TableCell className="py-1.5 text-xs tabular-nums">
                  {row.last_login_at ? (
                    formatRelativeAge(row.last_login_at)
                  ) : (
                    <span className="text-muted-foreground">never</span>
                  )}
                </TableCell>
                <TableCell className="py-1.5 text-right font-mono text-xs tabular-nums">
                  {formatTokenCount(row.total_tokens ?? 0)}
                </TableCell>
                <TableCell className="py-1.5 text-right font-mono text-xs tabular-nums">
                  {formatUserCost(row)}
                </TableCell>
                <TableCell
                  className="py-1.5 text-right"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <div className="flex items-center justify-end gap-0.5">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={handlers.onAuditLog}
                      disabled={anyBusy}
                      aria-label="View audit log"
                      title="View audit log"
                    >
                      <ScrollText className="h-3 w-3" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={handlers.onPurge}
                      disabled={anyBusy || handlers.isSelf}
                      aria-label="Delete user and all data"
                      title="Delete user & all data"
                    >
                      {handlers.busy === 'purge' ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Eraser className="h-3 w-3" />
                      )}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={handlers.onDelete}
                      disabled={anyBusy || handlers.isSelf}
                      aria-label="Delete user"
                      title="Delete user"
                    >
                      {handlers.busy === 'delete' ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
